// client/super/EventCreate.tsx
import { useState } from "react";
import { api, type EventOption } from "../lib/api";
import { fromInputValue } from "../lib/dates";
import { useNavigate } from "react-router-dom";
import HomeButton from "../components/HomeButton";

const CURRENCY = "RUB";
const MAX_OPTIONS = 5;
const MAX_SUBTYPES = 5;

type Subtype = {
  title: string;
  price_rub: string | number;
  description?: string;
  disabled?: boolean;
  available_at?: string; // datetime-local
};

type FormOption = {
  title: string;
  price_rub: string | number;
  starts_at: string;
  ends_at: string;
  description?: string;
  use_subtypes: boolean;
  disabled?: boolean;
  available_at?: string; // datetime-local
  subtypes: Subtype[];
};

type Form = {
  title: string;
  venue: string;
  city: string;
  starts_at: string;
  ends_at: string;
  status: "draft" | "active" | "archived";

  qr_valid_for_minutes: number | string;
  payee_name: string;
  payment_link: string;
  pay_note: string;
  // альтернативные способы + подписи
  pay_phone: string;
  pay_card: string;
  pay_sbp_url: string;
  pay_phone_label: string;
  pay_card_label: string;
  pay_sbp_label: string;

  pay_code_prefix: string;
  eula_required: boolean;
  eula_text: string;

  options: FormOption[];
};

export default function EventCreate() {
  const nav = useNavigate();

  const [form, setForm] = useState<Form>({
    title: "",
    venue: "",
    city: "",
    starts_at: "",
    ends_at: "",
    status: "active",

    qr_valid_for_minutes: 1440,
    payee_name: "",
    payment_link: "",
    pay_note: "",
    pay_phone: "",
    pay_card: "",
    pay_sbp_url: "",
    pay_phone_label: "",
    pay_card_label: "",
    pay_sbp_label: "",

    pay_code_prefix: "",
    eula_required: true,
    eula_text: "",

    options: [{
      title: "Основное мероприятие",
      price_rub: "",
      starts_at: "",
      ends_at: "",
      description: "",
      use_subtypes: false,
      disabled: false,
      available_at: "",
      subtypes: [],
    }],
  });

  const [saving, setSaving] = useState(false);

  const on = (k: keyof Form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const onBool = (k: keyof Form) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.checked }));

  function validate(): string | null {
    if (!form.title.trim()) return "Введите название мероприятия";
    if (!form.starts_at) return "Укажите дату и время начала события";
    if (!form.ends_at) return "Укажите дату и время окончания события";

    if (!form.payee_name.trim()) return "Укажите получателя";
    if (
      !form.payment_link.trim() &&
      !form.pay_phone.trim() &&
      !form.pay_card.trim() &&
      !form.pay_sbp_url.trim()
    ) {
      return "Укажите хотя бы один способ оплаты (ссылка/карта/телефон/СБП)";
    }

    if (form.options.length < 1) return "Добавьте как минимум один вид билета";
    if (form.options.length > MAX_OPTIONS) return "Слишком много видов билетов (максимум 5)";

    for (let i = 0; i < form.options.length; i++) {
      const o = form.options[i];
      if (!o.title.trim()) return `Вид #${i + 1}: укажите название`;
      const p = Number(o.price_rub);
      if (!o.use_subtypes) {
        if (!Number.isFinite(p) || p < 0) return `Вид #${i + 1}: цена должна быть ≥ 0`;
      }
      if (o.subtypes.length > MAX_SUBTYPES) return `Вид #${i + 1}: слишком много подвидов (максимум 5)`;
      for (let j = 0; j < o.subtypes.length; j++) {
        const s = o.subtypes[j];
        if (!s.title.trim()) return `Вид #${i + 1} / Подвид #${j + 1}: укажите название`;
        const sp = Number(s.price_rub);
        if (!Number.isFinite(sp) || sp < 0) return `Вид #${i + 1} / Подвид #${j + 1}: цена должна быть ≥ 0`;
      }
    }

    const pref = String(form.pay_code_prefix || "").trim();
    if (pref && !/^[A-Z]{4,6}$/.test(pref.toUpperCase())) return "Префикс должен быть 4–6 заглавных латинских букв (A–Z)";
    return null;
  }

  function addOption() {
    setForm((s) => {
      if (s.options.length >= MAX_OPTIONS) return s;
      return {
        ...s,
        options: [...s.options, {
          title: `Билет ${s.options.length + 1}`,
          price_rub: "",
          starts_at: "",
          ends_at: "",
          description: "",
          use_subtypes: false,
          disabled: false,
          available_at: "",
          subtypes: [],
        }],
      };
    });
  }

  function removeOption(idx: number) {
    setForm((s) => {
      const arr = s.options.slice();
      if (arr.length <= 1) return s;
      arr.splice(idx, 1);
      return { ...s, options: arr };
    });
  }

  function setOption<K extends keyof FormOption>(idx: number, key: K, value: FormOption[K]) {
    setForm((s) => {
      const arr = s.options.slice();
      arr[idx] = { ...arr[idx], [key]: value } as any;
      return { ...s, options: arr };
    });
  }

  function addSubtype(optIdx: number) {
    setForm((s) => {
      const arr = s.options.slice();
      const o = { ...arr[optIdx] };
      if (o.subtypes.length >= MAX_SUBTYPES) return s;
      o.use_subtypes = true;
      o.subtypes = [...o.subtypes, { title: `Подвид ${o.subtypes.length + 1}`, price_rub: "", description: "", disabled: false, available_at: "" }];
      arr[optIdx] = o;
      return { ...s, options: arr };
    });
  }

  function removeSubtype(optIdx: number, subIdx: number) {
    setForm((s) => {
      const arr = s.options.slice();
      const o = { ...arr[optIdx] };
      const subs = o.subtypes.slice();
      subs.splice(subIdx, 1);
      o.subtypes = subs;
      o.use_subtypes = subs.length > 0;
      arr[optIdx] = o;
      return { ...s, options: arr };
    });
  }

  function setSubtype(optIdx: number, subIdx: number, patch: Partial<Subtype>) {
    setForm((s) => {
      const arr = s.options.slice();
      const o = { ...arr[optIdx] };
      const subs = o.subtypes.slice();
      subs[subIdx] = { ...subs[subIdx], ...patch };
      o.subtypes = subs;
      arr[optIdx] = o;
      return { ...s, options: arr };
    });
  }

  async function submit() {
    const err = validate();
    if (err) return alert(err);

    setSaving(true);
    try {
      const payloadOptions: EventOption[] = form.options.map((o, i) => ({
        title: o.title.trim(),
        price_rub: o.use_subtypes ? 0 : Number(o.price_rub),
        starts_at: o.starts_at ? fromInputValue(o.starts_at) : null,
        ends_at:   o.ends_at   ? fromInputValue(o.ends_at)   : null,
        position: i,
        description: o.description || null,
        disabled: Boolean(o.disabled),
        available_at: o.available_at ? fromInputValue(o.available_at) : null,
        suboptions: o.subtypes.map((s, j) => ({
          title: s.title.trim(),
          price_rub: Number(s.price_rub),
          description: s.description || null,
          position: j,
          disabled: Boolean(s.disabled),
          available_at: s.available_at ? fromInputValue(s.available_at) : null,
        })),
      }));

      await api.events.create({
        title: form.title.trim(),
        venue: form.venue.trim() || null,
        city: form.city.trim() || null,
        starts_at: fromInputValue(form.starts_at),
        ends_at: fromInputValue(form.ends_at),
        status: form.status,
        currency: CURRENCY,
        qr_valid_for_minutes: Number(form.qr_valid_for_minutes) || 1440,
        payee_name: form.payee_name.trim() || null,
        payment_link: form.payment_link.trim() || null,
        pay_note: form.pay_note.trim() || null,
        // alt methods + labels
        pay_phone: form.pay_phone.trim() || null,
        pay_card: form.pay_card.trim() || null,
        pay_sbp_url: form.pay_sbp_url.trim() || null,
        pay_phone_label: form.pay_phone_label.trim() || null,
        pay_card_label: form.pay_card_label.trim() || null,
        pay_sbp_label: form.pay_sbp_label.trim() || null,
        pay_code_prefix: (String(form.pay_code_prefix || "").trim().toUpperCase() || null),
        eula_required: Boolean(form.eula_required),
        eula_text: form.eula_text === "" ? null : form.eula_text,
        options: payloadOptions,
      });

      nav("/super/events", { replace: true });
    } catch (e: any) {
      alert(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ui-card p-6">
      <h2 className="mb-4 text-[24px] font-bold">Создать мероприятие</h2>

      <FormGrid>
        <Input label="Название *" value={form.title} onChange={on("title")} />
        <Input label="Площадка" value={form.venue} onChange={on("venue")} />
        <Input label="Город" value={form.city} onChange={on("city")} />
        <Input type="datetime-local" label="Начало *" value={form.starts_at} onChange={on("starts_at")} />
        <Input type="datetime-local" label="Конец *"  value={form.ends_at}  onChange={on("ends_at")} />

        <Select
          label="Статус"
          value={form.status}
          onChange={on("status")}
          options={[["draft","Черновик"],["active","Активно"],["archived","Архив"]]}
        />

        <Input type="number" label="QR TTL (мин)" value={form.qr_valid_for_minutes} onChange={on("qr_valid_for_minutes")} min={1} />

        <Input label="Получатель *" value={form.payee_name} onChange={on("payee_name")} />
        <Input label="Реквизиты для оплаты *" value={form.payment_link} onChange={on("payment_link")} placeholder="ссылка, номер карты…" />

        {/* Альтернативные способы оплаты + подписи (необязательно) */}
        <div className="col-span-full mt-2 rounded-2xl border border-white/10 p-3">
          <div className="text-sm font-semibold text-white mb-2">Альтернативные способы оплаты</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input label="Телефон (P2P)" value={form.pay_phone} onChange={on("pay_phone")} placeholder="+7…" />
            <Input label="Номер карты" value={form.pay_card} onChange={on("pay_card")} placeholder="0000 0000 0000 0000" />
            <Input label="Ссылка СБП" value={form.pay_sbp_url} onChange={on("pay_sbp_url")} placeholder="https://qr.nspk.ru/..." />
            <Input label="Метка телефона (банк)" value={form.pay_phone_label} onChange={on("pay_phone_label")} placeholder="Тинькофф" />
            <Input label="Метка карты (банк)" value={form.pay_card_label} onChange={on("pay_card_label")} placeholder="Альфа-банк" />
            <Input label="Метка СБП" value={form.pay_sbp_label} onChange={on("pay_sbp_label")} placeholder="СБП Банк 131" />
          </div>
          <div className="mt-2 text-xs text-white/60">Укажите любую комбинацию. Эти подписи будут показаны гостям рядом с кнопками.</div>
        </div>

        <Input
          label="Префикс кода (A–Z, 4–6)"
          value={form.pay_code_prefix}
          onChange={(e) => setForm((s) => ({ ...s, pay_code_prefix: e.target.value.toUpperCase() }))}
          placeholder="URBS"
        />

        <Area label="Примечание (для гостей)" value={form.pay_note} onChange={on("pay_note")} />

        <label className="col-span-full flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.eula_required} onChange={onBool("eula_required")} className="h-4 w-4 rounded accent-emerald-600" />
          <span className="text-neutral-300">Требовать принятие оферты (EULA)</span>
        </label>

        <Area
          label="Текст оферты"
          value={form.eula_text}
          onChange={(e) => setForm((s) => ({ ...s, eula_text: e.target.value }))}
          placeholder={"Например:\n1) Правила участия...\n2) Возврат средств...\n3) Прочее..."}
          className="min-h-[120px] whitespace-pre-wrap"
        />
      </FormGrid>

      {/* ВАРИАНТЫ БИЛЕТОВ */}
      <div className="mt-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Варианты билетов</h3>
          <button
            onClick={addOption}
            disabled={form.options.length >= MAX_OPTIONS}
            className="ui-btn-sm disabled:opacity-50"
          >
            + Добавить вид
          </button>
        </div>

        {form.options.map((o, idx) => (
          <div key={idx} className="ui-panel bg-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm opacity-80">Вид #{idx + 1}</div>
              <button
                onClick={() => removeOption(idx)}
                disabled={form.options.length <= 1}
                className="ui-btn-sm text-xs disabled:opacity-50"
              >
                Удалить вид
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Название *" value={o.title} onChange={(e) => setOption(idx, "title", e.target.value)} />
              <Input
                type="number"
                label={o.use_subtypes ? "Цена (задана в подвидах)" : "Цена, ₽ *"}
                value={o.price_rub as any}
                min={0}
                disabled={o.use_subtypes}
                onChange={(e) => setOption(idx, "price_rub", e.target.value)}
              />
              <Input type="datetime-local" label="Начало (опц.)" value={o.starts_at} onChange={(e) => setOption(idx, "starts_at", e.target.value)} />
              <Input type="datetime-local" label="Конец (опц.)"   value={o.ends_at}   onChange={(e) => setOption(idx, "ends_at", e.target.value)} />
              <Area className="sm:col-span-2" label="Описание вида (опц.)" value={o.description || ""} onChange={(e) => setOption(idx, "description", e.target.value)} />

              {/* NEW: управление доступностью вида */}
              <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!o.disabled}
                  onChange={(e) => setOption(idx, "disabled", e.target.checked)}
                  className="h-4 w-4 rounded accent-emerald-600"
                />
                <span className="text-neutral-300">Отключить вид (СКОРО)</span>
              </label>
              <Input
                type="datetime-local"
                label="Доступно с (опц.)"
                value={o.available_at || ""}
                onChange={(e) => setOption(idx, "available_at", e.target.value)}
              />
            </div>

            {/* Подвиды */}
            <div className="ui-panel p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">Подвиды</div>
                <button
                  type="button"
                  onClick={() => addSubtype(idx)}
                  disabled={o.subtypes.length >= MAX_SUBTYPES}
                  className="ui-btn-sm text-xs disabled:opacity-50"
                >
                  + Подвид
                </button>
              </div>

              <div className="space-y-3">
                {o.subtypes.map((st, j) => (
                  <div key={j} className="grid gap-2 sm:grid-cols-[1fr_minmax(120px,220px)_auto] items-end">
                    <Input
                      label={`Подвид #${j + 1}: название *`}
                      value={st.title}
                      onChange={(e) => setSubtype(idx, j, { title: e.target.value })}
                      className="min-w-0"
                    />
                    <Input
                      type="number"
                      label="Цена, ₽ *"
                      value={st.price_rub as any}
                      min={0}
                      onChange={(e) => setSubtype(idx, j, { price_rub: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => removeSubtype(idx, j)}
                      className="self-end h-[38px] rounded-lg border border-white/15 px-3 text-xs whitespace-nowrap hover:bg-white/10"
                    >
                      Удалить
                    </button>
                    <Area
                      label="Описание подвида (опц.)"
                      value={st.description || ""}
                      onChange={(e) => setSubtype(idx, j, { description: e.target.value })}
                      className="sm:col-span-3 whitespace-pre-wrap"
                    />

                    {/* NEW: выключение и отложенное включение */}
                    <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!st.disabled}
                        onChange={(e) => setSubtype(idx, j, { disabled: e.target.checked })}
                        className="h-4 w-4 rounded accent-emerald-600"
                      />
                      <span className="text-neutral-300">Отключить подвид (СКОРО)</span>
                    </label>
                    <Input
                      type="datetime-local"
                      label="Доступно с (опц.)"
                      value={st.available_at || ""}
                      onChange={(e) => setSubtype(idx, j, { available_at: e.target.value })}
                    />
                  </div>
                ))}
                {!o.subtypes.length && (
                  <div className="text-xs text-white/60">Подвиды не добавлены (необязательно).</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex gap-3">
        <HomeButton />
        <button
          onClick={submit}
          disabled={saving}
          className="btn-primary"
        >
          Создать
        </button>
      </div>
    </div>
  );
}

function FormGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}
function Input(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, className, ...rest } = props;
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-neutral-300">{label}</span>
      <input {...rest} className={`ui-input ${className || ""}`} />
    </label>
  );
}
function Select(props: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string; options: [string,string][] }) {
  const { label, options, className, ...rest } = props;
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-neutral-300">{label}</span>
      <select {...rest} className={`ui-input ${className || ""}`}>
        {options.map(([v,t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );
}
function Area(props: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string }) {
  const { label, className, ...rest } = props;
  return (
    <label className="col-span-full grid gap-1 text-sm">
      <span className="text-neutral-300">{label}</span>
      <textarea {...rest} className={`ui-input min-h-[84px] ${className || ""}`} />
    </label>
  );
}
